import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Tabs from '../ui/Tabs';
import ListPageToolbar from '../ui/ListPageToolbar';
import SearchInput from '../ui/SearchInput';
import WeekPicker, { shiftWeek } from '../ui/WeekPicker';
import useWeekSlide from './useWeekSlide';
import GroupControl from '../ui/GroupControl';
import SortableHeader from '../ui/SortableHeader';
import { GroupHeader, useGroupedList } from '../ui/GroupedList';
import PhoneActionBar from '../ui/PhoneActionBar';
import LoadingSpinner from '../LoadingSpinner';
import EmptyState from '../EmptyState';
import ConfirmDialog from '../ConfirmDialog';
import RequiredReasonDialog from '../RequiredReasonDialog';
import UnitWeekTimecard, { appliedUnitIds, orderUnitsByCrew, unitHeadCount } from './UnitWeekTimecard';
import PhoneContextBar, { SheetField } from './PhoneContextBar';
import ChecklistDropdown from '../ui/ChecklistDropdown';
import SegmentedControl from '../ui/SegmentedControl';
import { SubmitReportBody, submitReport } from './submitWeekReport';
import PersonWeekGrid, { draftsDirty, draftsToBodies, personConflictItems } from './PersonWeekGrid';
import AwayDialog from './AwayDialog';
import TimesheetActionsMenu, { payrollRunRequest } from './TimesheetActionsMenu';
import TimesheetExportDialog from './TimesheetExportDialog';
import useTimesheetExport from './useTimesheetExport';
import useEditReason from './useEditReason';
import TimesheetHistoryPanel from './TimesheetHistoryPanel';
import ChangedBadges from './ChangedBadges';
import { useIsMobileOrderViewport } from '../../hooks/useIsMobileOrderViewport';
import { scrollUnderPinnedChrome } from '../../hooks/useOpenAtTop';
import { BTN, BTN_XS } from '../../constants/buttons';
import { moneyOrAbsent } from '../../utils/moneyOrAbsent';
import { formatDateDayMonth, toLocalISO } from '../../utils/dateFormat';
import {
  lockDepartmentWeek, createAwayPeriod, endAwayPeriod, fetchManagerWeek, reopenPersonWeek,
  resolveConflictItems, saveManagerPersonDay, submitPersonWeek,
} from '../../utils/timesheetsApi';
import {
  CREW_STATE_PILL_COLOURS, CREW_STATE_TABS, STATUS_PILL_COLOURS, WEEK_STATUS_TABS, awayReasonLabel, awayRefusalText, callWrapText, fieldLabel, gagPickList,
  skipReasonText, weekStatusLabel, weekdayShort,
  saveErrorText, unitNameIn, listedAsConflicting,
} from '../../utils/timesheetDisplay';

/**
 * THE MANAGER SCREEN (spec §2; owner-approved mock 2026-10-25): for anyone
 * holding `canManageBudget` in the ACTIVE department.
 *
 *   [🔍 Search people] [‹ w/c 19 Oct 2026 ›] [Units & Sub-depts │ All users] [Group ▾]   [Actions ▾]
 *   🔀 2026-10-28: the sketch above showed an Actions ▾ that was NEVER rendered until the
 *   timesheet exports (`TimesheetActionsMenu`: Export timesheets… + this week's payroll run,
 *   `TimesheetExportDialog`, `GET /api/timesheets/export`). On a phone the same actions sit
 *   in the context sheet.
 *   [All · To complete · Conflicting · Rate missing · Reconciled · Submitted · Away]   ← All users
 *
 * The toolbar is `ui/ListPageToolbar` — the Breakdown's composition, shared.
 * "Units & Sub-depts" holds the ONE unit timecard (`UnitWeekTimecard`);
 * "All users" the people, grouped by sub-department (or flat), each expanding
 * to their week on the same grid (`PersonWeekGrid`). Both panels stay MOUNTED
 * (hidden), so switching tab never drops typed values.
 *
 * Conflicts (owner, no banner): an amber ▲ on the person's row and on the
 * cell; the cell opens a chooser; "Keep all theirs" / "Use all unit values"
 * sit beside Approve. Every one is the existing resolve request.
 * 🔀 2026-10-25: the manager's "Submit week" is labelled APPROVE ("costs
 * fixed"); the crew member's own hand-in shows as a Sent / Taken over pill and
 * two extra tabs — a SECOND axis beside the status, never in its precedence.
 * Saving a change to a person's draft week takes it over (server-side).
 *
 * 💷 Money shown here is ONLY the server's: a submitted week's frozen
 * `actual_total`, an open week's `projected.total`, a day's `cost` /
 * `committed`. Nothing is multiplied in the browser.
 *
 * Unsaved person edits: the PAGE owns the three-way prompt; this screen
 * publishes save/discard into `saveRef`/`discardRef` and routes every click
 * that would lose them through `continueWhenClean`. A week change also asks
 * the unit timecard's prompt (`continueWhenTimecardClean`).
 */

const ALL = 'all';
const UNITS_TAB = 'units';
const PEOPLE_TAB = 'people';
const GROUP_OPTIONS = [
  { id: 'sub_department', label: 'Sub-department' },
  { id: 'flat', label: 'Flat list' },
];
const NO_UNIT = 'none';

function MoneyCell({ person }) {
  if (person.status === 'submitted') {
    // EDIT AFTER SUBMIT (2026-10-29): the actual is the LATEST figure; an edited week also says what
    // it was when first submitted (the mock's "(was £1,100.00 when submitted)").
    const was = person.changed_since_submit ? person.changed_since_submit.submitted_total : null;
    return (
      <span data-testid={`person-actual-${person.lineage_id}`}>
        <span className="block text-xs text-gray-500 dark:text-gray-400">Actual</span>
        <span className="font-medium tabular-nums text-gray-900 dark:text-gray-100">{moneyOrAbsent(person.actual_total)}</span>
        {was != null && was !== person.actual_total && (
          <span className="block text-xs tabular-nums text-gray-500 dark:text-gray-400" data-testid={`person-was-${person.lineage_id}`}>was {moneyOrAbsent(was)}</span>
        )}
      </span>
    );
  }
  const p = person.projected || {};
  return (
    <span data-testid={`person-projected-${person.lineage_id}`}>
      <span className="block text-xs text-gray-500 dark:text-gray-400">Projected</span>
      <span className="tabular-nums text-gray-800 dark:text-gray-200">{p.total == null ? 'Rate missing' : moneyOrAbsent(p.total)}</span>
    </span>
  );
}

/** A collapsed group must never hide a problem: head-count, then ▲ conflicting / to complete when > 0. */
function GroupSummary({ people }) {
  const n = people.length;
  const conflicting = people.filter(listedAsConflicting).length;
  const notEntered = people.filter((p) => p.status === 'not_entered').length;
  const sent = people.filter((p) => p.crew_state === 'sent' && p.status !== 'submitted').length;
  return (
    <span className="text-xs text-gray-600 dark:text-gray-400" data-testid="people-group-summary">
      · {n} {n === 1 ? 'person' : 'people'}
      {conflicting > 0 && <span className="font-medium text-amber-700 dark:text-amber-400"> · <span aria-hidden="true">▲</span> {conflicting} conflicting</span>}
      {notEntered > 0 && <span> · {notEntered} to complete</span>}
      {sent > 0 && <span> · {sent} sent</span>}
    </span>
  );
}

/** The hand-in pill beside the status (Sent / Taken over); nothing for a draft or an approved week. */
function CrewStatePill({ person }) {
  const s = person.crew_state;
  if (person.status === 'submitted' || (s !== 'sent' && s !== 'taken')) return null;
  return (
    <span data-testid={`person-crew-state-${person.lineage_id}`} className={`ml-1 rounded-full px-2 py-0.5 text-xs font-medium ${CREW_STATE_PILL_COLOURS[s]}`}>
      {s === 'sent' ? 'Sent' : 'Taken over'}
    </span>
  );
}

/** A people-list day: Away / Rest / call–wrap; a conflict adds the amber ▲. */
function DayCell({ day }) {
  let text;
  if (day.away) text = `Away · ${awayReasonLabel(day.away.reason)}`;
  else if (day.fields && day.fields.day_type && day.fields.day_type.value === 'rest') text = 'Rest';
  else text = callWrapText(day.fields) || (day.not_entered ? 'To complete' : null);
  const conflict = day.conflicts && day.conflicts.length > 0;
  if (conflict) {
    return (
      <span className="font-medium text-amber-700 dark:text-amber-400" title={`Differs from the unit timecard: ${day.conflicts.map(fieldLabel).join(', ')}`}>
        <span aria-hidden="true">▲ </span>{text || 'Own value'}<span className="sr-only"> — differs from the unit timecard</span>
      </span>
    );
  }
  if (text == null) return <span aria-hidden="true" className="text-gray-400 dark:text-gray-500">·</span>;
  return <span className={day.not_entered ? 'italic text-gray-500 dark:text-gray-400' : 'text-gray-800 dark:text-gray-200'}>{text}</span>;
}

export default function ManagerTimesheets({
  weekStart,
  onWeekChange,
  units,
  gags,
  continueWhenClean,
  continueWhenTimecardClean = (fn) => fn(),
  saveRef,
  discardRef,
  onDirtyChange,
  onTimecardDirtyChange,
  timecardSaveRef,
  timecardDiscardRef,
  modeControl = null,
}) {
  const isMobile = useIsMobileOrderViewport();
  // The unit timecard's Apply to, owned here so the phone sheet can set it (null = biggest unit).
  const [applyPicked, setApplyPicked] = useState(null);
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(UNITS_TAB);
  const [statusTab, setStatusTab] = useState(ALL);
  const [groupBy, setGroupBy] = useState('sub_department');
  const [sortDir, setSortDir] = useState('asc');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null); // lineage_id
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [submitAsk, setSubmitAsk] = useState(null); // { title, report, canSubmit, run }
  const [reopenFor, setReopenFor] = useState(null);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenError, setReopenError] = useState(null);
  const [awayFor, setAwayFor] = useState(null);
  const [awayError, setAwayError] = useState(null);
  const [endAwayFor, setEndAwayFor] = useState(null);
  const [endAwayDate, setEndAwayDate] = useState('');
  // Exports (2026-10-28): the dialog, its refusal, and THE runner (fetch → CSV/PDF).
  const [exportOpen, setExportOpen] = useState(false);
  const [exportError, setExportError] = useState(null);
  const { run: runExport, busy: exportBusy } = useTimesheetExport();
  // EDIT AFTER SUBMIT (2026-10-29): THE reason gate every manager save here runs through, and the
  // open History panel (one person-week at a time); `historyTick` re-reads it after every save.
  const { run: runEditGate, dialog: editReasonDialog } = useEditReason({ unitName: (id) => unitNameIn(units, id) });
  const [historyFor, setHistoryFor] = useState(null); // lineage_id
  const [historyTick, setHistoryTick] = useState(0);
  const seq = useRef(0);
  const today = toLocalISO(new Date());
  // Desktop: the list slides in from the side the picker moved to (owner 2026-10-30).
  const weekSlideRef = useWeekSlide(view ? view.week_start : null, weekStart);
  // 📱 (2026-10-31) the unit timecard slides with the week too (the phone end panels step it).
  const unitSlideRef = useWeekSlide(view ? view.week_start : null, weekStart);

  const load = useCallback(async ({ silent = false } = {}) => {
    const mine = ++seq.current;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const r = await fetchManagerWeek(weekStart);
      if (mine !== seq.current) return;
      if (!r.ok) { setError((r.body && r.body.error) || 'Could not load the week.'); return; }
      setView(r.body);
      setHistoryTick((t) => t + 1);
    } catch (e) {
      if (mine === seq.current) setError('Could not reach the server. Check your connection and try again.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  const dirty = draftsDirty(drafts);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const { gagByTitle, gagTitles } = useMemo(() => gagPickList(gags), [gags]);
  const people = useMemo(() => (view && view.people) || [], [view]);
  const counts = (view && view.counts) || {};
  const total = WEEK_STATUS_TABS.reduce((n, t) => n + (counts[t.id] || 0), 0);
  const statusTabs = [
    { id: ALL, label: 'All', count: total },
    // RC-2: a submitted week a crew knock-on moved is ALSO listed under Conflicting (the buyer decides).
    ...WEEK_STATUS_TABS.map((t) => ({ id: t.id, label: t.label, count: (counts[t.id] || 0) + (t.id === 'conflicting' ? people.filter((p) => p.status !== 'conflicting' && listedAsConflicting(p)).length : 0) })),
    // The crew hand-in axis (2026-10-25) — overlaps the status tabs on purpose
    // (a Sent week is ALSO Reconciled or To complete); counted here from the rows.
    ...CREW_STATE_TABS.map((t) => ({ id: t.id, label: t.label, count: people.filter((p) => p.crew_state === t.crewState && p.status !== 'submitted').length })),
  ];
  const crewTab = CREW_STATE_TABS.find((t) => t.id === statusTab) || null;
  const dates = (view && view.dates) || [];

  const q = search.trim().toLowerCase();
  const shown = people
    .filter((p) => (statusTab === ALL || (crewTab ? (p.crew_state === crewTab.crewState && p.status !== 'submitted')
      : (p.status === statusTab || (statusTab === 'conflicting' && listedAsConflicting(p)))))
      && (q === '' || String(p.name || '').toLowerCase().includes(q)))
    .sort((a, b) => (sortDir === 'asc' ? 1 : -1) * String(a.name || '').localeCompare(String(b.name || '')));

  const unitName = (id) => unitNameIn(units, id);
  const groups = useMemo(() => {
    if (groupBy === 'flat') return [{ key: 'all', label: null, people: shown }];
    const map = new Map();
    for (const p of shown) {
      const key = p.default_sub_department_id == null ? NO_UNIT : String(p.default_sub_department_id);
      if (!map.has(key)) map.set(key, { key, label: key === NO_UNIT ? 'No sub-department' : (unitName(key) || `Unit ${key}`), people: [] });
      map.get(key).people.push(p);
    }
    return [...map.values()].sort((a, b) => (a.key === NO_UNIT) - (b.key === NO_UNIT) || a.label.localeCompare(b.label));
  }, [groupBy, shown, units]); // eslint-disable-line react-hooks/exhaustive-deps
  // Owner 2026-10-25: "sub dept layout should be collapsed by default" — the
  // shared `ui/GroupedList` state (the Breakdown's), closed by default; while a
  // search is active every group with a match is FORCED open, and closes again
  // when the search clears. The header summary says what a closed group hides.
  const forceOpen = useCallback((g) => q !== '' && g.people.length > 0, [q]);
  const groupState = useGroupedList({ defaultOpen: false, forceOpen });

  const expandedPerson = people.find((p) => p.lineage_id === expanded) || null;

  const discard = useCallback(() => { setDrafts({}); setSaveError(null); }, []);
  const save = useCallback(async () => {
    if (!expandedPerson) return false;
    const { bodies, error: bodyError } = draftsToBodies(drafts, expandedPerson, gagByTitle);
    if (bodyError) { setSaveError(bodyError); return false; }
    setSaving(true);
    setSaveError(null);
    try {
      // 🔀 2026-10-29: ONE reason per Save, asked only when the save changes a SUBMITTED week
      // (`useEditReason`: the read-only preview, then the server's own refusal as the backstop).
      // Every day of the save carries it. A day already saved is never re-sent on the retry.
      const done = new Set();
      const sendAll = async (reason) => {
        let last = { ok: true };
        for (const { date, body } of bodies) {
          if (done.has(date)) continue;
          // eslint-disable-next-line no-await-in-loop
          const r = await saveManagerPersonDay(expandedPerson.lineage_id, date, reason ? { ...body, edit_reason: reason } : body);
          if (!r.ok) return r;
          done.add(date);
          last = r;
          setDrafts((prev) => { const next = { ...prev }; delete next[date]; return next; });
        }
        return last;
      };
      const r = await runEditGate(
        { kind: 'person', lineage_id: expandedPerson.lineage_id, days: bodies.map(({ date, body }) => ({ date, ...body })) },
        sendAll,
      );
      // "Keep editing": nothing was sent and the drafts stay exactly as they were.
      if (r && r.cancelled) { if (done.size) await load({ silent: true }); return false; }
      if (!r || !r.ok) { setSaveError(saveErrorText(r && r.body, 'Could not save this week.')); if (done.size) await load({ silent: true }); return false; }
      await load({ silent: true });
      return true;
    } catch (e) {
      setSaveError('Could not reach the server. Your changes are still here — try again.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [expandedPerson, drafts, gagByTitle, load, runEditGate]);

  useEffect(() => {
    if (saveRef) saveRef.current = save;
    if (discardRef) discardRef.current = discard;
  }, [save, discard, saveRef, discardRef]);

  // An opened person's PANEL lands just UNDER the pinned chrome: the summary row
  // scrolls away and the panel's action line names the person (owner 2026-10-25,
  // option A: the week and its buttons on one 1440×900 / 1536×864 screen).
  // Desktop only — the phone cards open in place.
  useEffect(() => {
    if (!expanded || isMobile || typeof document === 'undefined') return;
    const panel = document.querySelector(`[data-testid="person-panel-${expanded}"]`);
    scrollUnderPinnedChrome(panel);
  }, [expanded, isMobile]);

  const toggleExpand = (lineageId) => continueWhenClean(() => {
    setExpanded((cur) => (cur === lineageId ? null : lineageId));
    setDrafts({});
    setSaveError(null);
  });

  // `refusalInDialog`: the caller shows a refusal INSIDE its open dialog, so the
  // page banner stays quiet (a second copy behind the dialog outlives it).
  async function runAction(fn, okText, { refusalInDialog = false } = {}) {
    setActionBusy(true);
    setNotice(null);
    try {
      const r = await fn();
      if (!r.ok) {
        if (!refusalInDialog) setNotice({ tone: 'error', text: (r.body && r.body.error) || 'That did not work.' });
        return r;
      }
      if (okText) setNotice({ tone: 'ok', text: okText });
      await load({ silent: true });
      return r;
    } catch (e) {
      setNotice({ tone: 'error', text: 'Could not reach the server. Try again.' });
      return { ok: false };
    } finally {
      setActionBusy(false);
    }
  }

  /** One conflict pick from a cell's chooser — the existing items request. */
  // RC-3 (owner 2026-10-30, ruling A): settling a conflict is a manager save like any other — when
  // its 6th/7th run reaches a SUBMITTED week it asks for the reason (`useEditReason`, kind
  // 'conflicts'); "Keep editing" settles nothing.
  const settleThroughGate = (picked) => runEditGate(
    { kind: 'conflicts', items: picked },
    (editReason) => resolveConflictItems(picked, editReason),
  );
  const resolveItems = async (items, choice) => {
    const r = await settleThroughGate(items.map((it) => ({ ...it, choice })));
    if (r && r.ok) await load({ silent: true });
    return r;
  };
  const resolvePerson = (p, choice) => continueWhenClean(() => runAction(
    () => settleThroughGate(personConflictItems(p).map((it) => ({ ...it, choice })))
      .then((x) => (x && x.cancelled ? { ok: false, cancelled: true } : x)),
    choice === 'theirs' ? `${p.name}'s own values are kept.` : `${p.name} now follows the unit values.`,
    { refusalInDialog: true },
  ).then((r) => {
    if (r && !r.ok && !r.cancelled && r.body) setNotice({ tone: 'error', text: r.body.error || 'That did not work.' });
    return r;
  }));

  // SUBMIT WEEK (owner 2026-10-26): on the unit timecard (the people whose HOME
  // sub-department is in Apply to) and on one person — never on the All users list.
  // It SAVES first (the caller does), re-fetches the week, and warns before submitting
  // when anything is missing ("Missing details … are you sure?"): the server's own
  // required_blank (blocks) and gaps (warns) lists, via submitReport.
  const beginSubmit = async ({ unitIds = null, person = null }) => {
    let fresh;
    try { fresh = await fetchManagerWeek(weekStart); } catch (e) { fresh = { ok: false }; }
    if (!fresh.ok) { setNotice({ tone: 'error', text: 'Could not check the week. Try again.' }); return; }
    setView(fresh.body);
    const everyone = fresh.body.people || [];
    const scope = person ? everyone.filter((x) => x.lineage_id === person.lineage_id)
      : everyone.filter((x) => x.default_sub_department_id != null && unitIds.includes(Number(x.default_sub_department_id)));
    const report = submitReport(scope);
    const run = person
      ? () => runAction(() => submitPersonWeek(person.lineage_id, weekStart), `${person.name}'s week is submitted.`)
      : async () => { const r = await runAction(() => lockDepartmentWeek(weekStart, unitIds)); if (r && r.ok) setCommitResult(r.body); };
    const canSubmit = person ? (scope[0] && scope[0].status === 'reconciled') : true;
    if (report.empty && canSubmit) { await run(); return; }
    setSubmitAsk({
      title: report.empty ? 'Submit week?' : 'Missing details — are you sure you want to submit?',
      report, canSubmit, run,
      confirmLabel: person ? 'Submit week' : `Submit ${report.ready}`,
    });
  };
  const submitPerson = (p) => async () => {
    if (dirty && !(await save())) return;
    await beginSubmit({ person: p });
  };
  const showConflicting = () => { setCommitResult(null); setTab(PEOPLE_TAB); setStatusTab('conflicting'); };

  const changeWeek = (w) => continueWhenClean(() => continueWhenTimecardClean(() => onWeekChange(w)));
  const changeStatus = (id) => setStatusTab(id);
  const changeTab = (id) => setTab(id);

  const modeTabs = (className) => (
    <Tabs bare className={className}
      tabs={[{ id: UNITS_TAB, label: 'Units & Sub-depts' }, { id: PEOPLE_TAB, label: 'All users' }]}
      active={tab} onChange={changeTab} idPrefix="timesheets-view" ariaLabel="Timesheet view" />
  );
  const statusStrip = (extra = {}) => (
    <Tabs bare tabs={statusTabs} active={statusTab} onChange={changeStatus} idPrefix="timesheets-status"
      ariaLabel="Timesheet status" {...extra} />
  );
  const picker = <WeekPicker weekStart={weekStart} onChange={changeWeek} idPrefix="manager-week" />;

  const openExport = () => { setExportError(null); setExportOpen(true); };
  const runPayroll = async (format) => {
    setNotice(null);
    const r = await runExport(payrollRunRequest(weekStart, format), { payrollRun: true });
    if (!r.ok) setNotice({ tone: 'error', text: r.error });
    else afterExport(r);
  };
  // A recorded export clears "Changed since export" on the weeks it carried: re-read the week.
  function afterExport(r) {
    if (r.recordError) setNotice({ tone: 'error', text: r.recordError });
    load({ silent: true });
  }
  const actionsMenu = (variant = 'menu', close = null) => (
    <TimesheetActionsMenu variant={variant} weekStart={weekStart} busy={exportBusy} closeSheet={close}
      onOpenExport={openExport} onPayrollRun={runPayroll} />
  );

  const errorAlert = (
    <p role="alert" data-testid="manager-load-error" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200">{error}</p>
  );

  function renderPanel(p) {
    const submitted = p.status === 'submitted';
    const conflictItems = submitted ? [] : personConflictItems(p);
    const historyOpen = historyFor === p.lineage_id;
    // 📱 Below `md` the pair shows ONLY while changed (pinned); a disabled pair is just clutter there.
    // 🔀 2026-10-29 (owner, edit after submit): a SUBMITTED week is editable by the manager too —
    // its Save asks for a reason (`useEditReason`) — so the pair is there for every week.
    const saveBar = (
      <PhoneActionBar pinned={dirty} fill className={`ml-auto flex justify-end gap-2 ${dirty ? '' : 'max-md:hidden'}`} testId="manager-week-actions">
        <button type="button" className={BTN.secondary} disabled={!dirty || saving} onClick={discard}>Discard</button>
        <button type="button" data-testid="manager-week-save" className={BTN.confirm} disabled={!dirty || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </PhoneActionBar>
    );
    return (
      <div data-testid={`person-panel-${p.lineage_id}`} className="space-y-2 border-t border-gray-200 bg-gray-50 p-2 dark:border-gray-600 dark:bg-gray-700/30">
        <div className="flex flex-wrap items-center gap-2">
          {/* Whose week: the summary row has scrolled off above (desktop). */}
          <span data-testid="person-panel-name" className="mr-1 hidden text-sm font-semibold text-gray-900 md:inline dark:text-gray-100">{p.name}</span>
          <ChangedBadges person={p} />
          {p.crew_knock_on && (
            <span data-testid={`person-crew-knock-on-${p.lineage_id}`} className="text-xs font-medium text-amber-700 dark:text-amber-400">
              ▲ {p.crew_knock_on.reason} — Resubmit to accept it, or Reopen to correct it.
            </span>
          )}
          {submitted && p.changed_since_submit && (
            // RESUBMIT clears "Changed since submitted" (owner 2026-10-29) — saves first, like Submit week.
            <button type="button" data-testid="person-resubmit" className={BTN_XS.confirm}
              disabled={actionBusy || saving}
              title="Save, then submit this week again — it clears “Changed since submitted”"
              onClick={async () => {
                if (dirty && !(await save())) return;
                await runAction(() => submitPersonWeek(p.lineage_id, weekStart), `${p.name}'s week is resubmitted.`);
              }}>
              Resubmit week
            </button>
          )}
          {!submitted && (
            <button type="button" data-testid="person-submit" className={BTN_XS.confirm}
              disabled={actionBusy || saving}
              title="Save, then submit this week — crew can no longer change it and its costs are fixed"
              onClick={submitPerson(p)}>
              Submit week
            </button>
          )}
          {submitted && (
            <button type="button" data-testid="person-reopen" className={BTN_XS.warning} disabled={actionBusy}
              onClick={() => { setReopenReason(''); setReopenError(null); setReopenFor(p); }}>
              Reopen week
            </button>
          )}
          <button type="button" data-testid="person-away" className={BTN_XS.secondary} disabled={actionBusy}
            onClick={() => { setAwayError(null); setAwayFor(p); }}>
            Set away
          </button>
          <button type="button" data-testid="person-history" className={`${BTN_XS.secondary} max-md:min-h-[44px]`}
            aria-expanded={historyOpen} aria-controls={`person-history-${p.lineage_id}`}
            onClick={() => setHistoryFor((cur) => (cur === p.lineage_id ? null : p.lineage_id))}>
            History
          </button>
          {conflictItems.length > 0 && (
            <>
              <button type="button" data-testid="person-keep-theirs" className={BTN_XS.secondary} disabled={actionBusy}
                onClick={() => resolvePerson(p, 'theirs')}>
                Keep all theirs
              </button>
              <button type="button" data-testid="person-use-unit" className={BTN_XS.secondary} disabled={actionBusy}
                onClick={() => resolvePerson(p, 'unit')}>
                Use all unit values
              </button>
            </>
          )}
          {(p.away_periods || []).map((a) => (
            <span key={a.id} className="inline-flex items-center gap-2 rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-800 dark:bg-slate-700 dark:text-slate-100">
              Away ({awayReasonLabel(a.reason)}) {a.from_date} → {a.to_date || 'until further notice'}
              {a.to_date == null && (
                <button type="button" data-testid={`person-end-away-${a.id}`} className="underline min-h-[44px] md:min-h-0" disabled={actionBusy}
                  onClick={() => { setEndAwayDate(today >= a.from_date ? today : a.from_date); setEndAwayFor(a); }}>
                  End
                </button>
              )}
            </span>
          ))}
          {submitted && p.submitted_at && (
            <span className="text-xs text-gray-600 dark:text-gray-400">Submitted {p.submitted_at.slice(0, 10)}</span>
          )}
          {/* Discard / Save UP on the action row, top-right (owner 2026-10-25: fit a laptop screen). */}
          {!isMobile && saveBar}
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-2 md:px-2 md:py-1 dark:border-gray-700 dark:bg-gray-800">
          <PersonWeekGrid
            person={p}
            mode="manager"
            onStepWeek={(d) => changeWeek(shiftWeek(weekStart, d))}
            requestedWeek={weekStart}
            units={units}
            gagTitles={gagTitles}
            today={today}
            readOnly={saving}
            drafts={drafts}
            onDraftsChange={setDrafts}
            onResolve={resolveItems}
            idPrefix={`manager-week-${p.lineage_id}`}
          />
        </div>
        {saveError && <p role="alert" className="text-sm text-red-700 dark:text-red-300">{saveError}</p>}
        {historyOpen && (
          <div id={`person-history-${p.lineage_id}`}>
            <TimesheetHistoryPanel kind="person" lineageId={p.lineage_id} weekStart={weekStart} units={units}
              title={`History · ${p.name} · w/c ${formatDateDayMonth(weekStart)}`} refreshSignal={historyTick}
              onClose={() => setHistoryFor(null)} testId="person-history-panel" />
          </div>
        )}
        {/* 📱 On a phone the pinned bar sits at the END of the panel: its spacer then pads the panel's
            foot instead of growing the Submit week row (owner 2026-10-28: "margins and padding change"). */}
        {isMobile && saveBar}
      </div>
    );
  }

  const personRow = (p) => {
    const isOpen = expanded === p.lineage_id;
    return (
      <React.Fragment key={p.lineage_id}>
        <tr
          data-testid={`person-row-${p.lineage_id}`}
          className={`cursor-pointer border-b border-gray-100 dark:border-gray-700 ${isOpen
            ? '[&>td]:bg-gray-100 dark:[&>td]:bg-slate-700'
            : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}
          onClick={() => toggleExpand(p.lineage_id)}
        >
          <td className="w-8 p-2 text-gray-500 dark:text-gray-400" aria-hidden="true">{isOpen ? '▼' : '▶'}</td>
          <td className="p-2 font-medium text-gray-900 dark:text-gray-100">
            <button type="button" aria-expanded={isOpen} className="text-left"
              onClick={(e) => { e.stopPropagation(); toggleExpand(p.lineage_id); }}>
              {p.name}
            </button>
          </td>
          {p.days.map((d) => (
            <td key={d.date} className="whitespace-nowrap p-2 text-center text-sm tabular-nums" data-testid={`person-day-${p.lineage_id}-${d.date}`}>
              <DayCell day={d} />
            </td>
          ))}
          <td className="p-2">
            <span data-testid={`person-status-${p.lineage_id}`} className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_PILL_COLOURS[p.status] || ''}`}>
              {weekStatusLabel(p.status)}
            </span>
            <CrewStatePill person={p} />
            <ChangedBadges person={p} compact />
          </td>
          <td className="p-2 text-right"><MoneyCell person={p} /></td>
        </tr>
        {isOpen && (
          <tr data-expanded-panel>
            <td colSpan={11} className="p-0">{renderPanel(p)}</td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const peopleTable = (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-2 border-gray-200 dark:border-gray-700">
            <th className="w-8 p-2"><span className="sr-only">Expand</span></th>
            <SortableHeader label="Person" sortId="name" activeSort="name" sortDir={sortDir}
              onSort={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))} testId="timesheets-sort-person" />
            {dates.map((d) => (
              <th key={d} className="whitespace-nowrap p-2 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {weekdayShort(d)} {Number(d.slice(8, 10))}
              </th>
            ))}
            <SortableHeader label="Status" />
            <SortableHeader label="Cost" align="right" />
          </tr>
        </thead>
        {groups.map((g) => (
          <tbody key={g.key} data-testid={`people-group-${g.key}`}>
            {g.label != null && (
              <tr>
                <td colSpan={11} className="p-0">
                  <GroupHeader open={groupState.isOpen(g)} onToggle={() => groupState.toggle(g)} label={g.label}
                    testId={`people-group-header-${g.key}`}
                    summary={<GroupSummary people={g.people} />} />
                </td>
              </tr>
            )}
            {groupState.isOpen(g) && g.people.map(personRow)}
          </tbody>
        ))}
      </table>
    </div>
  );

  const peopleCards = (
    <ul className="divide-y divide-gray-100 dark:divide-gray-700">
      {shown.map((p) => {
        const isOpen = expanded === p.lineage_id;
        const conflicts = p.flags && p.flags.conflicting;
        return (
          <li key={p.lineage_id} data-testid={`person-card-${p.lineage_id}`}>
            <button type="button" aria-expanded={isOpen}
              className={`w-full p-3 text-left ${isOpen ? 'bg-gray-100 dark:bg-slate-700' : ''}`}
              onClick={() => toggleExpand(p.lineage_id)}>
              <div className="flex items-center gap-2">
                <span className="mr-auto font-medium text-gray-900 dark:text-gray-100">{p.name}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_PILL_COLOURS[p.status] || ''}`}>{weekStatusLabel(p.status)}</span>
              </div>
              {/* Shown ONCE (review m-7): an open card's panel, directly beneath, carries them. */}
              {!isOpen && (p.changed_since_submit || (p.export && p.export.changed_since_export_at)) && (
                <div className="mt-1 -ml-1"><ChangedBadges person={p} /></div>
              )}
              <div className="mt-1 flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
                <span>
                  {p.flags && p.flags.worked_days != null ? `${p.flags.worked_days} worked` : ''}
                  {p.flags && p.flags.not_entered ? ` · ${p.flags.not_entered} to complete` : ''}
                  {conflicts ? <span className="text-amber-700 dark:text-amber-400"> · ▲ {conflicts} to settle</span> : null}
                </span>
                <MoneyCell person={p} />
              </div>
            </button>
            {isOpen && renderPanel(p)}
          </li>
        );
      })}
    </ul>
  );

  // 📱 Phone header (mock B, owner 2026-10-27): one chip + week; everything else in its sheet.
  const viewUnits = (view && view.units) || [];
  const applyOrdered = orderUnitsByCrew(viewUnits, people);
  const applyIds = appliedUnitIds(applyPicked, applyOrdered);
  const applyLabel = applyIds.length === 0 ? 'No unit'
    : applyIds.length === 1 ? (applyOrdered.find((u) => u.id === applyIds[0]) || {}).label || 'Unit'
      : `${applyIds.length} units`;
  const applyCount = unitHeadCount(people, applyIds);
  const phoneSummary = tab === UNITS_TAB
    ? `${applyLabel} · Team · ${applyCount} ${applyCount === 1 ? 'person' : 'people'}`
    : `All users · ${shown.length}${q ? ` matching “${search.trim()}”` : ''}`;
  const phoneBar = (
    <PhoneContextBar idPrefix="manager" summary={phoneSummary} weekStart={weekStart} onWeekChange={changeWeek}
      // 🔍 on the header row, results visible below (owner 2026-10-28) — typing shows All users, as on desktop.
      search={{ text: search, label: 'Search people', placeholder: 'Search people…',
        onChange: (v) => { setSearch(v); if (v && tab !== PEOPLE_TAB) setTab(PEOPLE_TAB); } }}>
      {({ close }) => (
      <>
      {modeControl && <SheetField label="Timesheet">{modeControl}</SheetField>}
      <SheetField label="Show">
        <SegmentedControl options={[{ id: UNITS_TAB, label: 'Units & sub-depts' }, { id: PEOPLE_TAB, label: 'All users' }]}
          value={tab} onChange={changeTab} ariaLabel="Show" testId="timesheets-view-phone" />
      </SheetField>
      {tab === UNITS_TAB && (
        <SheetField label="Apply to">
          <ChecklistDropdown testId="manager-phone-apply-to" ariaLabel="Apply to" placeholder="Choose units…"
            sections={[{ key: 'units', items: applyOrdered }]} selected={applyIds}
            onChange={(ids) => setApplyPicked(ids.map(Number))}
            countNoun={(c) => `${c} ${c === 1 ? 'person' : 'people'}`} fullWidth />
        </SheetField>
      )}
      {/* The sheet sits ABOVE a modal, so each action closes it first. */}
      <SheetField label="Export">{actionsMenu('sheet', close)}</SheetField>
      </>
      )}
    </PhoneContextBar>
  );

  return (
    <div data-testid="manager-timesheets">
      {isMobile ? phoneBar : (
      <ListPageToolbar
        marginClassName="mb-2"
        data-testid="manager-toolbar"
        search={(
          <SearchInput value={search} onChange={(v) => { setSearch(v); if (v && tab !== PEOPLE_TAB) setTab(PEOPLE_TAB); }}
            placeholder="Search people…" ariaLabel="Search people" debounceMs={0} testId="timesheets-search" />
        )}
        picker={picker}
        tabs={modeTabs('shrink-0')}
        actions={actionsMenu()}
        {...(tab === PEOPLE_TAB ? {
          group: (
            <GroupControl options={GROUP_OPTIONS} value={groupBy} onChange={setGroupBy} flatId="flat"
              fallbackLabel="Sub-department" title="Group the people" testId="timesheets-group"
              // 📏 A short trigger label: with the week picker the 1280 bar is 63px over
              // and Actions ▾ wrapped to a row of its own (measured 2026-10-25). The
              // panel still heads "Group by" and names both options in full.
              renderLabel={(current) => (
                <span>{current === 'Sub-department' ? 'Sub-dept' : 'Flat list'}</span>
              )} />
          ),
          statusRow: statusStrip(),
        } : {})}
      />
      )}
      {isMobile && tab === PEOPLE_TAB && (
        <div className="mb-2 min-w-0 overflow-x-auto">{statusStrip({ scrollHints: true })}</div>
      )}

      {notice && (
        <p role={notice.tone === 'error' ? 'alert' : 'status'} data-testid="manager-notice"
          className={`mb-3 rounded-md p-3 text-sm ${notice.tone === 'error'
            ? 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200'
            : 'bg-emerald-50 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-100'}`}>
          {notice.text}
        </p>
      )}

      {loading && !view ? (
        <div className="py-10"><LoadingSpinner /></div>
      ) : error && !view ? (
        errorAlert
      ) : (
        <div className="space-y-4">
          {/* A failed REFETCH keeps the unit timecard MOUNTED (its typed values
              live in it) and hides only the people, which may be another
              week's now. A first-load failure has no timecard. */}
          {error && errorAlert}
          {/* Hidden, never unmounted: switching tab must not drop typed values. */}
          <div ref={unitSlideRef} hidden={tab !== UNITS_TAB} data-testid="timesheets-units-panel">
            <UnitWeekTimecard
              onStepWeek={(d) => changeWeek(shiftWeek(weekStart, d))}
              requestedWeek={weekStart}
              units={(view && view.units) || []}
              dates={dates}
              today={today}
              people={people}
              onDirtyChange={onTimecardDirtyChange}
              saveRef={timecardSaveRef}
              discardRef={timecardDiscardRef}
              onSaved={() => load({ silent: true })}
              onSubmitWeek={(unitIds) => beginSubmit({ unitIds })}
              dayNumbers={(view && view.day_numbers) || {}}
              submitBusy={actionBusy}
              picked={applyPicked}
              onPickedChange={setApplyPicked}
            />
          </div>
          <div hidden={tab !== PEOPLE_TAB} data-testid="timesheets-people-panel">
            {error ? null : shown.length === 0 ? (
              <EmptyState
                size="sm"
                title={people.length === 0 ? 'Nobody to show for this week' : 'Nobody in this tab'}
                description={people.length === 0
                  ? 'Crew come from the department’s current budget. Add crew there and they appear here.'
                  : 'Pick another status or clear the search to see more people.'}
                testId="manager-empty"
              />
            ) : (
              <div ref={weekSlideRef} className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800" data-testid="people-list">
                {isMobile ? peopleCards : peopleTable}
              </div>
            )}
          </div>
        </div>
      )}

      <RequiredReasonDialog
        open={!!reopenFor}
        title={reopenFor ? `Reopen ${reopenFor.name}'s week` : ''}
        message="The week leaves actual spend and goes back to Reconciled until it is submitted again."
        reason={reopenReason}
        onReasonChange={setReopenReason}
        reasonLabel="Why are you reopening it?"
        reasonMaxLength={2000}
        confirmLabel="Reopen week"
        confirmVariant="warning"
        busy={actionBusy}
        onCancel={() => { setReopenFor(null); setReopenError(null); }}
        onConfirm={async (reason) => {
          const r = await runAction(() => reopenPersonWeek(reopenFor.lineage_id, weekStart, reason), `${reopenFor.name}'s week is reopened.`);
          if (r && r.ok) { setReopenFor(null); setReopenReason(''); } else setReopenError(r && r.body && r.body.error);
        }}
      >
        {reopenError ? <p role="alert" className="text-sm text-red-700 dark:text-red-300">{reopenError}</p> : null}
      </RequiredReasonDialog>

      <TimesheetExportDialog
        open={exportOpen}
        weekStart={weekStart}
        units={viewUnits}
        people={people}
        busy={exportBusy}
        error={exportError}
        onCancel={() => setExportOpen(false)}
        onExport={async (request) => {
          setExportError(null);
          const r = await runExport(request);
          if (r.ok) { setExportOpen(false); afterExport(r); } else setExportError(r.error);
        }}
      />

      <AwayDialog
        open={!!awayFor}
        personName={awayFor && awayFor.name}
        defaultFrom={awayFor ? weekStart : ''}
        busy={actionBusy}
        error={awayError}
        onCancel={() => { setAwayFor(null); setAwayError(null); }}
        onConfirm={async (v) => {
          setAwayError(null);
          // 🔀 2026-10-29 (owner): Away follows the edit-after-submit rule — an Away touching a
          // SUBMITTED week asks for the reason first (Keep editing leaves this dialog open).
          const person = awayFor;
          const r = await runAction(() => runEditGate(
            { kind: 'away', lineage_id: person.lineage_id, from_date: v.fromDate, to_date: v.toDate, reason: v.reason },
            (editReason) => createAwayPeriod({ lineageId: person.lineage_id, ...v, editReason }),
          ).then((x) => (x && x.cancelled ? { ok: false, cancelled: true } : x)), `${person.name} is set as away.`, { refusalInDialog: true });
          if (r && r.cancelled) return;
          if (r && r.ok) setAwayFor(null); else setAwayError(awayRefusalText(r && r.body));
        }}
      />

      <ConfirmDialog
        open={!!endAwayFor}
        title="End this Away period"
        confirmLabel="End period"
        busy={actionBusy}
        confirmDisabled={!endAwayDate || (endAwayFor && endAwayDate < endAwayFor.from_date)}
        onCancel={() => setEndAwayFor(null)}
        onConfirm={async () => {
          const period = endAwayFor;
          const r = await runAction(() => runEditGate(
            { kind: 'away_end', away_id: period.id, to_date: endAwayDate },
            (editReason) => endAwayPeriod(period.id, endAwayDate, editReason),
          // Keep editing is NOT a success (review m-6): `ok: false` so `runAction` reloads nothing,
          // and `refusalInDialog` so it posts no error notice either — the dialog simply stays open.
          ).then((x) => (x && x.cancelled ? { ok: false, cancelled: true } : x)), null, { refusalInDialog: true });
          if (r && r.cancelled) return;
          if (r && !r.ok && r.body) { setNotice({ tone: 'error', text: r.body.error || 'That did not work.' }); return; }
          if (r && r.ok) { setEndAwayFor(null); setNotice({ tone: 'ok', text: 'The Away period is ended.' }); }
        }}
      >
        <label className="block text-sm text-gray-800 dark:text-gray-200">
          Last day away
          <input type="date" data-testid="end-away-date" className="mt-1 block min-h-[44px] md:min-h-0 rounded-md border border-gray-300 bg-white px-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            value={endAwayDate} min={endAwayFor ? endAwayFor.from_date : undefined} onChange={(e) => setEndAwayDate(e.target.value)} />
        </label>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!submitAsk}
        title={submitAsk ? submitAsk.title : ''}
        confirmLabel={submitAsk && submitAsk.canSubmit ? submitAsk.confirmLabel : 'Close'}
        hideCancel={!!submitAsk && !submitAsk.canSubmit}
        onConfirm={async () => { const ask = submitAsk; setSubmitAsk(null); if (ask && ask.canSubmit) await ask.run(); }}
        onCancel={() => setSubmitAsk(null)}
      >
        {submitAsk && <SubmitReportBody report={submitAsk.report} />}
      </ConfirmDialog>

      {/* Last, so it opens OVER the Away dialogs that can call it. */}
      {editReasonDialog}

      <ConfirmDialog
        open={!!commitResult}
        title={`Week w/c ${weekStart} submitted`}
        confirmLabel="Done"
        hideCancel
        onConfirm={() => setCommitResult(null)}
        onCancel={() => setCommitResult(null)}
      >
        {commitResult && (
          <div className="space-y-3 text-left text-sm" data-testid="commit-result">
            <p className="text-base text-gray-900 dark:text-gray-100" data-testid="commit-submitted-count">
              <strong>{commitResult.approved.length}</strong> submitted
            </p>
            {(() => {
              const n = commitResult.locked_for_crew.filter((s) => s.status === 'conflicting').length;
              return n > 0 ? (
                <p className="text-base font-medium text-amber-700 dark:text-amber-400" data-testid="commit-conflict-count">
                  <span aria-hidden="true">▲ </span><strong>{n}</strong> {n === 1 ? 'conflict needs' : 'conflicts need'} to be reconciled
                  <button type="button" onClick={showConflicting} className="ml-2 text-sm text-blue-700 underline hover:no-underline dark:text-blue-300">Show them</button>
                </p>
              ) : null;
            })()}
            <p className="text-gray-600 dark:text-gray-400">Crew can no longer change this week. You still can.</p>
            {commitResult.locked_for_crew.length > 0 && (
              <div>
                <p className="font-medium text-gray-800 dark:text-gray-200">Still open for you ({commitResult.locked_for_crew.length}):</p>
                <ul className="mt-1 space-y-1" data-testid="commit-skipped">
                  {commitResult.locked_for_crew.map((s) => (
                    <li key={s.lineage_id} className="flex flex-col sm:flex-row sm:justify-between sm:gap-3 text-gray-700 dark:text-gray-300">
                      <span>{s.name}</span>
                      <span className="text-gray-600 dark:text-gray-400 sm:text-right">{skipReasonText(s.status || s.reason)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
}
